export class CartManager {
    constructor(cartDrawer) {
        this.cartDrawer = cartDrawer;
        this._goodieSyncLock = false;
    }

    serializeObject(form) {
        let data = new FormData(form),
            obj = {};
        for (let [key, value] of data) {
            if (value === "") continue;
            // More than one form field with the same name
            if (obj[key] !== undefined) {
                if (!Array.isArray(obj[key])) {
                    obj[key] = [obj[key]];
                }
                obj[key].push(value);
            } else {
                obj[key] = value;
            }
        }
        return obj;
    }

    async addToCart(variantId, quantity = 1, openDrawer = true, element, bundle = null) {
        const productProperties = element.getAttribute("data-properties") || null;
        const productData = {
            id: variantId,
            quantity: quantity,
            ...productProperties && { properties: JSON.parse("{" + productProperties + "}") },
        };

        if (bundle) {
            const formData = this.serializeObject(element.closest("form"));
            const errorMessageContainer = document.getElementById("error-message-container");
            const formDataUniqueSkus = {};
            const formDataUniqueNames = {};

            if (
                typeof formData.id == "undefined"
                || typeof formData.quantity == "undefined"
                || typeof formData.sku == "undefined"
                || typeof formData.setQuantity == "undefined"
                || !Array.isArray(formData.name)
                || !Array.isArray(formData.sku)
                || !Array.isArray(formData.sku) && Array.isArray(formData.setQuantity)
                || Array.isArray(formData.sku) && !Array.isArray(formData.setQuantity)
                || Array.isArray(formData.sku) && Array.isArray(formData.setQuantity)
                    && formData.sku.length != formData.setQuantity.length
            ) {
                if (errorMessageContainer) {
                    errorMessageContainer.style.display = "block";
                }
                return;
            }

            if (errorMessageContainer) {
                errorMessageContainer.style.display = "none";
            }

            formData.sku.forEach(function(x) {
                formDataUniqueSkus[x] = (formDataUniqueSkus[x] || 0) + 1;
            });

            formData.name.forEach(function(x) {
                formDataUniqueNames[String(x)] = (formDataUniqueNames[String(x)] || 0) + 1;
            });

            productData["properties"] = {
                "_items": JSON.stringify(formDataUniqueSkus),
            };
            for (let key in formDataUniqueNames) {
                productData["properties"][key] = String(formDataUniqueNames[key]);
            }
        }

        const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [productData] }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "Failed to add to cart");
        }

        await this.refreshCart();
        if (openDrawer) this.openDrawer();

        return response.json();
    }

    async syncGoodies(cart, doc) {
        if (this._goodieSyncLock) return false;
        const barEl = (doc && doc.querySelector && doc.querySelector("goodie-bar"))
            || (this.cartDrawer && this.cartDrawer.querySelector
                && this.cartDrawer.querySelector("goodie-bar"))
            || document.querySelector("goodie-bar");
        if (!barEl) return false;

        this._goodieSyncLock = true;
        let modified = false;

        try {
            for (let i = 1; i <= 4; i++) {
                // Get variant id for this threshold
                const idCandidates = [
                    `data-goodie-id-${i}`,
                    `goodie-id-${i}`,
                    `data-goodieid-${i}`,
                    `goodieid-${i}`,
                    `data-goodie-${i}`,
                ];
                let gid = null;
                for (const name of idCandidates) {
                    const v = barEl.getAttribute(name);
                    if (v) {
                        gid = v.trim();
                        break;
                    }
                }
                if (!gid) continue;

                // Get threshold value for this goodie
                const thCandidates = [
                    `data-goodie-threshold-${i}`,
                    `goodie-threshold-${i}`,
                    `data-goodieThreshold-${i}`,
                    `goodieThreshold-${i}`,
                    `data-goodie-th-${i}`,
                ];
                let th = 0;
                for (const name of thCandidates) {
                    const v = barEl.getAttribute(name);
                    if (v) {
                        th = parseInt(v, 10) || 0;
                        break;
                    }
                }
                if (!th) continue;

                // Unique property for this threshold
                const propertyKey = `cart-${i}`;
                const meets = (cart.total_price || 0) >= th;

                // Find goodie line for this threshold (propertyKey)
                const goodieLine = (cart.items || []).find(item =>
                    String(item.variant_id) === String(gid)
                    && item.properties
                    && item.properties["_goodie"] === propertyKey
                );

                if (meets) {
                    if (!goodieLine) {
                        // Add goodie for this threshold
                        await fetch("/cart/add.js", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                items: [{
                                    id: gid,
                                    quantity: 1,
                                    properties: { "_goodie": propertyKey },
                                }],
                            }),
                        });
                        modified = true;
                    } else if (goodieLine.quantity > 1) {
                        // Normalize to quantity 1
                        await fetch("/cart/change.js", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ id: goodieLine.key, quantity: 1 }),
                        });
                        modified = true;
                    }
                } else {
                    if (goodieLine) {
                        // Remove goodie for this threshold
                        await fetch("/cart/change.js", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ id: goodieLine.key, quantity: 0 }),
                        });
                        modified = true;
                    }
                }
            }
        } catch (err) {
            console.error("syncGoodies error", err);
        } finally {
            this._goodieSyncLock = false;
        }

        return modified;
    }

    async updateQuantity(lineKey, quantity) {
        const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: lineKey, quantity }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "Failed to update cart");
        }

        await this.refreshCart();
        return response.json();
    }

    async removeItem(lineKey) {
        return this.updateQuantity(lineKey, 0);
    }

    async refreshCart() {
        // Fetch latest cart and drawer HTML
        const [cartResponse, drawerResponse] = await Promise.all([
            fetch("/cart.js"),
            fetch("/?section_id=section-cart-drawer"),
        ]);
        const cart = await cartResponse.json();

        // Parse drawer HTML
        let doc = null;
        if (drawerResponse.ok) {
            const html = await drawerResponse.text();
            const parser = new DOMParser();
            doc = parser.parseFromString(html, "text/html");
        }

        // Sync goodies with up-to-date cart and doc
        let goodiesModified = false;
        try {
            goodiesModified = await this.syncGoodies(cart, doc);
        } catch (err) {
            console.error("Error syncing goodies", err);
        }

        // If goodies were modified, fetch again to reflect changes
        if (goodiesModified) {
            return this.refreshCart();
        }

        // Update cart count
        const countEl = document.querySelector("#cart-count");
        if (countEl) countEl.textContent = cart.item_count;

        // Update drawer content
        if (doc && this.cartDrawer) {
            const newContent = doc.querySelector(".cart-drawer-content");
            const currentContent = this.cartDrawer.querySelector(".cart-drawer-content");
            const newFooter = doc.querySelector(".cart-footer");
            const currentFooter = this.cartDrawer.querySelector(".cart-footer");

            if (newContent && currentContent) {
                currentContent.innerHTML = newContent.innerHTML;
            }
            if (newFooter && currentFooter) {
                currentFooter.innerHTML = newFooter.innerHTML;
            }
        }
    }

    openDrawer() {
        if (!this.cartDrawer) return;
        const alpineEl = this.cartDrawer.closest("[x-data]");
        if (alpineEl?._x_dataStack?.[0]) {
            alpineEl._x_dataStack[0].open = true;
        }
    }
}

// Global functions for inline handlers
let cartManager;

export function initCartManager(cartDrawerElement) {
    cartManager = new CartManager(cartDrawerElement);

    // Enhanced global handlers with loading states
    window.updateQuantity = async (element, lineKey, qty) => {
        const cartItem = element.closest("[data-line-key]");
        const removeButton = cartItem.querySelector("button[onclick*=\"removeItem\"]");

        try {
            // Show loading states
            element.disabled = true;
            if (removeButton) {
                removeButton.disabled = true;
                const icon = removeButton.querySelector("[data-remove-icon]");
                const spinner = removeButton.querySelector("[data-remove-spinner]");
                if (icon) icon.classList.add("hidden");
                if (spinner) spinner.classList.remove("hidden");
            }

            // Wait for actual completion
            await cartManager.updateQuantity(lineKey, qty);
        } catch (error) {
            console.error(error);
            // alert("Failed to update quantity");

            // Reset on error only
            element.disabled = false;
            if (removeButton) {
                removeButton.disabled = false;
                const icon = removeButton.querySelector("[data-remove-icon]");
                const spinner = removeButton.querySelector("[data-remove-spinner]");
                if (icon) icon.classList.remove("hidden");
                if (spinner) spinner.classList.add("hidden");
            }
        }
        // No finally block - let the cart refresh handle the state reset
    };

    window.removeItem = async (element, lineKey) => {
        const cartItem = element.closest("[data-line-key]");
        const selectElement = cartItem.querySelector("select");

        try {
            // Show loading states
            element.disabled = true;
            if (selectElement) selectElement.disabled = true;

            const icon = element.querySelector("[data-remove-icon]");
            const spinner = element.querySelector("[data-remove-spinner]");
            if (icon) icon.classList.add("hidden");
            if (spinner) spinner.classList.remove("hidden");

            cartItem.style.opacity = "0.5";
            cartItem.style.transition = "opacity 0.3s ease";

            // Wait for actual completion
            await cartManager.removeItem(lineKey);
        } catch (error) {
            // Restore on error only
            cartItem.style.opacity = "1";
            element.disabled = false;
            if (selectElement) selectElement.disabled = false;

            const icon = element.querySelector("[data-remove-icon]");
            const spinner = element.querySelector("[data-remove-spinner]");
            if (icon) icon.classList.remove("hidden");
            if (spinner) spinner.classList.add("hidden");

            console.error(error);
            // alert("Failed to remove item");
        }
        // No finally block - successful operations will refresh the cart content
    };

    // Add the handleAddToCartClick helper
    window.handleAddToCartClick = async function(btn, variantId, qty = 1) {
        if (!cartManager || btn.disabled) return;

        const spinner = btn.querySelector("[data-spinner]");
        const label = btn.querySelector("[data-label]");

        try {
            btn.disabled = true;
            btn.classList.add("opacity-70");
            if (spinner) spinner.classList.remove("hidden");
            if (label) label.classList.add("sr-only");

            const bundle = btn.hasAttribute("data-bundle") ? true : null;
            await cartManager.addToCart(variantId, qty, true, btn, bundle);
        } catch (error) {
            console.error(error);
            // alert("Failed to add to cart");
        } finally {
            btn.disabled = false;
            btn.classList.remove("opacity-70");
            if (spinner) spinner.classList.add("hidden");
            if (label) label.classList.remove("sr-only");
        }
    };

    return cartManager;
}
